import type { ReactNode } from 'react';
import { ActionDialog } from './ActionDialog';
import type { ActionState, EnterpriseQuote, PartnerBillingView } from './pb-types';
import { day, quoteTerms } from './pb-words';

/** What every confirm on the Customer page gets: the company, its partner billing, the dialog's state. */
export interface CompanyDialogProps extends ActionState {
  company: string;
  billing: PartnerBillingView | null;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

const payer = (b: PartnerBillingView | null): string => b?.partner ?? 'its partner';

/** Pause one company its partner pays for: a service switch, never a stop of billing. */
export function PauseCompanyDialog(p: CompanyDialogProps): ReactNode {
  return (
    <ActionDialog
      {...p}
      title={`Pause ${p.company}`}
      confirmLabel="Pause company"
      danger
      consequence={
        <>
          <p>
            {p.company} goes to <strong>Free</strong> now and its admins are told its plan with{' '}
            {payer(p.billing)} is paused. Plan changes and packs are refused until it resumes.
          </p>
          <p>
            <strong>Billing carries on</strong>: {payer(p.billing)} is still billed at each renewal
            {p.billing?.renewsOn ? ` (next ${day(p.billing.renewsOn)})` : ''}. To stop billing, end
            partner billing instead.
          </p>
        </>
      }
    />
  );
}

export function ResumeCompanyDialog(p: CompanyDialogProps): ReactNode {
  return (
    <ActionDialog
      {...p}
      title={`Resume ${p.company}`}
      confirmLabel="Resume company"
      consequence={
        <p>
          {p.company} goes back to its plan now, with its full pages for the period, and its admins
          are told. If the paying account is itself suspended, the company stays on Free until that
          is resumed too.
        </p>
      }
    />
  );
}

/** Staff only: partner billing ends today, nothing refunded for the rest of a period billed in advance. */
export function EndNowDialog(p: CompanyDialogProps): ReactNode {
  return (
    <ActionDialog
      {...p}
      title={`End partner billing for ${p.company} now`}
      confirmLabel="End partner billing now"
      danger
      consequence={
        <>
          <p>
            {p.company} goes to <strong>Free</strong> today and becomes a direct customer: it buys
            its own plan on its Billing page from now on.
          </p>
          <p>
            {p.billing?.invoiceChannel === 'quickbooks'
              ? `Its period was billed in advance on ${payer(p.billing)}'s statement and nothing is refunded. If Ademero agrees a credit, add an adjustment on the partner account page.`
              : `Its item comes off ${payer(p.billing)}'s online subscription now.`}
          </p>
        </>
      }
    />
  );
}

export function CallOffMoveDialog(p: CompanyDialogProps): ReactNode {
  const m = p.billing?.cardMove;
  return (
    <ActionDialog
      {...p}
      title="Call off the card move"
      confirmLabel="Call off the move"
      danger
      consequence={
        <p>
          The move on {day(m?.takesOverOn)} does not happen: {p.company}&rsquo;s card plan renews on
          its own again, and its partner and its admins are told. Nothing is billed to the partner.
        </p>
      }
    />
  );
}

export function WithdrawQuoteDialog(p: CompanyDialogProps & { quote: EnterpriseQuote }): ReactNode {
  return (
    <ActionDialog
      {...p}
      reason={false}
      title={`Withdraw ${p.quote.quoteRef ?? 'the quote'}`}
      confirmLabel="Withdraw quote"
      danger
      consequence={
        <p>
          The offer of {quoteTerms(p.quote)} can no longer be taken. Nothing is billed; a quote is
          never edited, so new terms are a new quote.
        </p>
      }
    />
  );
}
